import {Injectable} from '@angular/core';
import {EnvironmentColors} from 'src/app/models/environment-colors.model';
import {EnvironmentProfile} from 'src/app/models/environment-profile.enum';
import {environment} from 'src/environments/environment';
import {BatchlineSelection} from '../../models/batchline-selection';

@Injectable({
  providedIn: 'root',
})
export class ColorDataService {
  // factor for shading component colors if more than 16 somponents should be displayed
  private componentShadeFactor: number = 30;

  private chartColors = [
    '#B369DE',
    '#F9C80E',
    '#35A775',
    '#4297CF',
    '#F49900',
    '#8CD1FF',
    '#FF99C9',
    '#3DCCC7',
    '#E15886',
    '#8EA604',
    '#C1BDDB',
    '#E3E3E3',
    '#FFFF00',
    '#9370DB',
    '#FF6666',
    '#339933',
  ];

  private chartBorderColors = [
    '#6B3B86',
    '#786210',
    '#196946',
    '#1A4A69',
    '#785720',
    '#3F5B6D',
    '#834461',
    '#175B59',
    '#7F2D48',
    '#3D4802',
    '#4C4866',
    '#626262',
    '#5B3B86',
    '#686210',
    '#834461',
    '#336600',
  ];

  private outlierColor = [
    '#6B3B86',
    '#786210',
    '#196946',
    '#1A4A69',
    '#785720',
    '#3F5B6D',
    '#834461',
    '#175B59',
    '#7F2D48',
    '#3D4802',
    '#4C4866',
    '#626262',
    '#5B3B86',
    '#686210',
    '#834461',
    '#336600',
  ];

  private devColors: EnvironmentColors = {
    /*primaryColor: '#f39500',
    lightPrimaryColor: '#ffe700',
    primaryLineColor: '#eeb150',
    lightPrimaryLineColor: '#f3e458',*/
    // Surventis colors for chart lines matched to the DEV theme
    primaryColor: '#d93b1d',
    lightPrimaryColor: '#ff724c',
    primaryLineColor: '#d93b1d',
    lightPrimaryLineColor: '#ff724c',
  };

  private qualColors: EnvironmentColors = {
    /*primaryColor: '#c50022',
    lightPrimaryColor: '#ee6e84',
    primaryLineColor: '#d72a47',
    lightPrimaryLineColor: '#ff9eaf',*/
    // Surventis colors for chart lines matched to the QUAL theme
    primaryColor: '#9c009f',
    lightPrimaryColor: '#d460dd',
    primaryLineColor: '#9c009f',
    lightPrimaryLineColor: '#d460dd',
  };

  private prodColors: EnvironmentColors = {
    primaryColor: '#004a96',
    lightPrimaryColor: '#4ea6ff',
    primaryLineColor: '#4777a8',
    lightPrimaryLineColor: '#92c8ff',
    // for now prod will use the same blue theme as before because carlos prefers the blue over teal
    // Surventis colors for chart lines matched to the PROD theme
    /*primaryColor: '#007e6a',
    lightPrimaryColor: '#00be99',
    primaryLineColor: '#007e6a',
    lightPrimaryLineColor: '#00be99',*/
  };

  private multiBatchColors = [
    '#004a96',
    '#f39500',
    '#c50022',
    '#339933',
    '#B369DE',
    '#7F2D48',
  ]

  constructor() {}

  public getChartColors(): string[] {
    return this.chartColors;
  }

  public getChartBorderColors(): string[] {
    return this.chartBorderColors;
  }

  public getOutlierColor(): string[] {
    return this.outlierColor;
  }

  public getEnvironmentColors(): EnvironmentColors {
    switch (environment.profile) {
      case EnvironmentProfile.DEVELOPMENT:
        return this.devColors;
      case EnvironmentProfile.QUAL:
        return this.qualColors;
      default:
        return this.prodColors;
    }
  }

  public getChartComponentColors(allComponents: string[]): string[] {
    const compColors: string[] = [];
    if (!allComponents || allComponents.length === 0) {
      return compColors;
    }

    for (let i = 0; i < allComponents.length; i++) {
      if (i < this.chartColors.length) {
        compColors[allComponents[i]] = this.chartColors[i];
      } else {
        compColors[allComponents[i]] = this.shadeColor(this.chartColors[i % this.chartColors.length], (Math.floor(i / this.chartColors.length) * this.componentShadeFactor));
      }
    }

    return compColors;
  }

  public getChartComponentBorderColors(allComponents: string[]): string[] {
    const compColors: string[] = [];
    if (!allComponents || allComponents.length === 0) {
      return compColors;
    }

    for (let i = 0; i < allComponents.length; i++) {
      if (i < this.chartBorderColors.length) {
        compColors[allComponents[i]] = this.chartBorderColors[i];
      } else {
        compColors[allComponents[i]] = this.shadeColor(this.chartBorderColors[i % this.chartBorderColors.length], (Math.floor(i / this.chartBorderColors.length) * this.componentShadeFactor));
      }
    }

    return compColors;
  }

  public getChartComponentOutlierColors(allComponents: string[]): string[] {
    const compColors: string[] = [];
    if (!allComponents || allComponents.length === 0) {
      return compColors;
    }

    for (let i = 0; i < allComponents.length; i++) {
      if (i < this.outlierColor.length) {
        compColors[allComponents[i]] = this.outlierColor[i];
      } else {
        compColors[allComponents[i]] = this.shadeColor(this.outlierColor[i % this.outlierColor.length], (Math.floor(i / this.outlierColor.length) * this.componentShadeFactor));
      }
    }

    return compColors;
  }

  public getMultiBatchColors(batchLineSelections: BatchlineSelection[]): string[] {
    const multiBatchColors: string[] = [];
    if (!batchLineSelections || batchLineSelections.length === 0) {
      return multiBatchColors;
    }

    batchLineSelections.forEach((batchLineSelection: BatchlineSelection, index: number) => {
      if (index < this.multiBatchColors.length) {
        multiBatchColors[batchLineSelection.batchId + '-' + batchLineSelection.line] = this.multiBatchColors[index];
      } else {
        multiBatchColors[batchLineSelection.batchId + '-' + batchLineSelection.line] = "#ffffff";
      }
    });

    return multiBatchColors;
  }

  public getSimpleMultiBatchColors(): string[] {
    return this.multiBatchColors;
  }

  /*
  // method is currently not used but will perhaps in the future
  private addAlphaToColor(color: string, alpha: number): string {
    // Convert hex color to RGB values
    const red = parseInt(color.slice(1, 3), 16);
    const green = parseInt(color.slice(3, 5), 16);
    const blue = parseInt(color.slice(5, 7), 16);

    // Create RGBA color string
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  }*/

  private shadeColor(color: string, percent: number): string {

    var R = parseInt(color.substring(1, 3), 16);
    var G = parseInt(color.substring(3, 5), 16);
    var B = parseInt(color.substring(5, 7), 16);

    R = (R * (100 + percent) / 100);
    G = (G * (100 + percent) / 100);
    B = (B * (100 + percent) / 100);

    R = (R<255)?R:255;
    G = (G<255)?G:255;
    B = (B<255)?B:255;

    R = Math.round(R)
    G = Math.round(G)
    B = Math.round(B)

    var RR = ((R.toString(16).length === 1)?"0"+R.toString(16):R.toString(16));
    var GG = ((G.toString(16).length === 1)?"0"+G.toString(16):G.toString(16));
    var BB = ((B.toString(16).length === 1)?"0"+B.toString(16):B.toString(16));

    return "#"+RR+GG+BB;
  }

  public lightenColor(color: string, percent: number): string {
    // Extract RGB values from hex string
    let R = parseInt(color.substring(1, 3), 16);
    let G = parseInt(color.substring(3, 5), 16);
    let B = parseInt(color.substring(5, 7), 16);

    // Increase each channel toward 255 (white) by the given percent
    R = R + ((255 - R) * (percent / 100));
    G = G + ((255 - G) * (percent / 100));
    B = B + ((255 - B) * (percent / 100));

    // Clamp values to 255 and round
    R = Math.round(R > 255 ? 255 : R);
    G = Math.round(G > 255 ? 255 : G);
    B = Math.round(B > 255 ? 255 : B);

    // Convert back to hex with leading zeros if needed
    const RR = (R.toString(16).length === 1 ? "0" + R.toString(16) : R.toString(16));
    const GG = (G.toString(16).length === 1 ? "0" + G.toString(16) : G.toString(16));
    const BB = (B.toString(16).length === 1 ? "0" + B.toString(16) : B.toString(16));

    return "#" + RR + GG + BB;
  }


  /**
   * Returns the closest matching colored circle emoji for a given color string.
   *
   * The color is expected to be a HEX value and is mapped to a predefined palette of chart colors.
   * Used for displaying colors in text-only contexts (e.g., tooltips).
   *
   * @param color HEX color string (e.g. '#F49900')
   * @returns A Unicode circle emoji representing the closest color match
   */

  public getChartColorCircle(color: string): string {
    if (!color) return '⚪';

    const c = color.toUpperCase();

    // explicit mapping for the palette of chart colors
    const map: Record<string, string> = {
      '#B369DE': '🟣', // purple
      '#F9C80E': '🟡', // yellow
      '#35A775': '🟢', // green
      '#4297CF': '🔵', // blue
      '#F49900': '🟠', // orange
      '#8CD1FF': '🔵', // light blue → blue
      '#FF99C9': '🟣', // pink → purple
      '#3DCCC7': '🟢', // teal → green
      '#E15886': '🔴', // pink/red → red
      '#8EA604': '🟢', // olive → green
      '#C1BDDB': '🟣', // light purple
      '#E3E3E3': '⚪', // gray → white
      '#FFFF00': '🟡', // yellow
      '#9370DB': '🟣', // purple
      '#FF6666': '🔴', // red
      '#339933': '🟢', // green
    };

    return map[c] ?? '⚪';
  }

}
